import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap border text-sm font-semibold transition-colors duration-200 disabled:pointer-events-none disabled:opacity-45 [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'border-primary bg-primary text-primary-foreground hover:bg-transparent hover:text-primary',
        outline:
          'border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground',
        ghost: 'border-transparent bg-transparent text-primary hover:bg-primary/10',
        secondary:
          'border-border bg-secondary text-secondary-foreground hover:border-primary/60',
        destructive:
          'border-destructive bg-destructive text-destructive-foreground hover:bg-transparent hover:text-destructive',
        link: 'border-0 bg-transparent p-0 text-primary underline underline-offset-4 hover:text-primary/80',
      },
      size: {
        default: 'h-11 px-7 py-3',
        sm: 'h-9 px-4 text-xs',
        lg: 'h-12 px-8 text-base',
        icon: 'size-10 p-0',
        none: 'p-0',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & { asChild?: boolean };

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}

export { buttonVariants };
